import PropTypes from 'prop-types';
import React, { useEffect, useState } from "react";
import { Button, Label, Modal } from 'react-bootstrap';
import { NavLink } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import moment from 'moment';
import { HandleApiError } from "../../../components/Notifications/APIErrorHandler";
import { Contract, Expand, Link, Date as DateIcon } from "../../../components/Icons/Icons";

// dd/mm/aaaa con padding de ceros; a diferencia de FromToViewModel.From/To (backend), que no
// paddea y usa "unk" para partes faltantes -- acá se prioriza el formato pedido por el usuario.
const formatPartialDate = (day, month, year) => {
    if (!year) return '';
    const d = day ? String(day).padStart(2, '0') : 'unk';
    const m = (month !== null && month !== undefined) ? String(month + 1).padStart(2, '0') : 'unk';
    return `${d}/${m}/${year}`;
};

// Devuelve un string plano (no JSX) -- t() de i18next stringifica cualquier valor de interpolacion,
// y un elemento React ahi adentro se convierte en "[object Object]" en vez del texto formateado.
const formatDateTime = (dateTime) => dateTime ? moment(dateTime).format('DD/MM/YYYY') : '';

// Historial completo por concepto clinico: muestra TODOS los ciclos (administracion/cierre) del
// mismo ConceptId para el paciente, no solo el ciclo de la fila clickeada (reemplaza al viejo
// historial por logicalItemId, que solo mostraba un ciclo puntual).
// La API devuelve una lista de ciclos (uno por LogicalItemId), cada ciclo con su cadena de versiones.
export const ModalConceptHistory = (props) => {
    const { t } = useTranslation();
    const { conceptId, medicalRecordNumber, useHistoryHook, getConceptName, getCycleSubtitle } = props;

    const [isLoading, setIsLoading] = useState(true);
    const [cycles, setCycles] = useState([]);
    const [expanded, setExpanded] = useState(true);
    const [fetchHistory] = useHistoryHook();

    useEffect(() => {
        let isSubscribed = true;
        const fetchData = async () => {
            try {
                const res = await fetchHistory(medicalRecordNumber, conceptId);
                if (isSubscribed) {
                    setCycles(res.data || []);
                    setIsLoading(false);
                }
            } catch (error) {
                HandleApiError(error);
                setIsLoading(false);
                setCycles([]);
            }
        }

        fetchData();

        return () => isSubscribed = false;
    }, [conceptId, medicalRecordNumber]);

    if (isLoading)
        return null;

    // Cada ciclo es una cadena de versiones (alta -> ediciones -> cierre) de un mismo LogicalItemId;
    // isOpen se deriva de toYear (no de isCurrent, que solo indica "no superseded", no "sin cerrar").
    const cycleSummaries = cycles.map((cycle) => {
        const firstVersion = cycle[0];
        const lastVersion = cycle[cycle.length - 1];
        return {
            fromYear: firstVersion.fromYear,
            fromLabel: formatPartialDate(firstVersion.fromDay, firstVersion.fromMonth, firstVersion.fromYear),
            toLabel: formatPartialDate(lastVersion.toDay, lastVersion.toMonth, lastVersion.toYear),
            isOpen: !lastVersion.toYear,
            // Si abrio y cerro en la misma visita (ej. carga retroactiva ya cerrada), alcanza un solo link.
            sameVisit: firstVersion.entryId === lastVersion.entryId,
            subtitle: getCycleSubtitle(lastVersion),
            versions: cycle,
            firstVersion,
            lastVersion,
        };
    });

    const openCycle = cycleSummaries.find((c) => c.isOpen);
    const conceptName = cycleSummaries.length > 0 ? getConceptName(cycleSummaries[cycleSummaries.length - 1].lastVersion) : '';

    return (
        <Modal show={true} onHide={props.onClose} bsSize="large">
            <Modal.Header closeButton>
                <Modal.Title>{props.title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="concept-history-card">
                    <header className="concept-history-card__header" onClick={() => setExpanded(!expanded)}>
                        <div className="concept-history-card__title">
                            <Link />
                            <span className="concept-history-card__name">{conceptName}</span>
                            {openCycle
                                ? <Label bsStyle="success">{t('medicalRecords.history.vigenteDesde', { year: openCycle.fromYear })}</Label>
                                : <Label bsStyle="default">{t('medicalRecords.history.finalizado')}</Label>
                            }
                        </div>
                        <button className="btn-table-actions" type="button" onClick={() => setExpanded(!expanded)}>
                            {expanded ? <Contract /> : <Expand />}
                        </button>
                    </header>
                    {expanded &&
                        <div className="concept-history-card__body">
                            {cycleSummaries.map((cycle, index) => (
                                <div className="concept-history-cycle" key={cycle.lastVersion.logicalItemId || index}>
                                    <div>
                                        <div className="concept-history-cycle__range">
                                            {cycle.fromLabel} — {cycle.isOpen ? t('medicalRecords.history.actualidad') : cycle.toLabel}
                                        </div>
                                        {cycle.subtitle && <div className="concept-history-cycle__subtitle">{cycle.subtitle}</div>}
                                        <div className="concept-history-cycle__visits">
                                            {cycle.isOpen ? (
                                                <NavLink
                                                    to={`/admin/medicalRecords/${medicalRecordNumber}/entry/${cycle.firstVersion.entryId}`}
                                                    className="nav-link"
                                                    activeClassName="active"
                                                >
                                                    <DateIcon />
                                                    {t('medicalRecords.history.openedOn', { date: formatDateTime(cycle.firstVersion.modified) })}
                                                </NavLink>
                                            ) : cycle.sameVisit ? (
                                                <NavLink
                                                    to={`/admin/medicalRecords/${medicalRecordNumber}/entry/${cycle.lastVersion.entryId}`}
                                                    className="nav-link"
                                                    activeClassName="active"
                                                >
                                                    <DateIcon />
                                                    {t('medicalRecords.history.openedAndClosedOn', { date: formatDateTime(cycle.lastVersion.modified) })}
                                                </NavLink>
                                            ) : (
                                                <>
                                                    <NavLink
                                                        to={`/admin/medicalRecords/${medicalRecordNumber}/entry/${cycle.firstVersion.entryId}`}
                                                        className="nav-link"
                                                        activeClassName="active"
                                                    >
                                                        <DateIcon />
                                                        {t('medicalRecords.history.opened', { date: formatDateTime(cycle.firstVersion.modified) })}
                                                    </NavLink>
                                                    <span className="concept-history-cycle__visits-separator">·</span>
                                                    <NavLink
                                                        to={`/admin/medicalRecords/${medicalRecordNumber}/entry/${cycle.lastVersion.entryId}`}
                                                        className="nav-link"
                                                        activeClassName="active"
                                                    >
                                                        <DateIcon />
                                                        {t('medicalRecords.history.closed', { date: formatDateTime(cycle.lastVersion.modified) })}
                                                    </NavLink>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <Label bsStyle={cycle.isOpen ? 'success' : 'default'}>
                                        {t(cycle.isOpen ? 'medicalRecords.history.vigente' : 'medicalRecords.history.finalizado')}
                                    </Label>
                                </div>
                            ))}
                        </div>
                    }
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button onClick={props.onClose}>{t('buttons.close')}</Button>
            </Modal.Footer>
        </Modal>
    );
}

ModalConceptHistory.propTypes = {
    onClose: PropTypes.func.isRequired,
    conceptId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    useHistoryHook: PropTypes.func.isRequired,
    getConceptName: PropTypes.func.isRequired,
    getCycleSubtitle: PropTypes.func.isRequired,
    medicalRecordNumber: PropTypes.string,
    title: PropTypes.string,
};
